import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
export class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // Media rendered directly (not behind a deferred <template> poster gate) already exists as a
    // <video> child by the time this component upgrades. Bind the hint to its own play/pause
    // events (see #bindVideo) instead of assuming it's playing from the `autoplay` attribute alone
    // - that assumption doesn't hold when autoplay is blocked or still buffering, which left the
    // hint stuck out of sync with the actual video.
    const video = this.querySelector(':scope > video');
    if (video instanceof HTMLVideoElement) {
      this.#bindVideo(video);
    } else if (this.hasAttribute('autoplay') && this.querySelector(':scope > iframe[data-video-type]')) {
      // No reliable play/pause event is available for a cross-origin iframe player, so this is the
      // one remaining place the play state is assumed from the `autoplay` attribute.
      this.isPlaying = true;
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Binds the play/pause hint to a <video> element's own play/pause events, so the hint (and
   * `isPlaying`) are always derived from the video's actual state rather than an assumption made
   * at the call site - whether that's the `autoplay` attribute at connect time, or optimistically
   * flipping the hint before the video has actually responded to play()/pause().
   * @param {HTMLVideoElement} video
   */
  #bindVideo(video) {
    const signal = this.#abortController.signal;
    video.addEventListener('play', () => this.#setPlaying(true), { signal });
    video.addEventListener('pause', () => this.#setPlaying(false), { signal });
    this.#setPlaying(!video.paused);
  }

  /**
   * @param {boolean} isPlaying
   */
  #setPlaying(isPlaying) {
    this.isPlaying = isPlaying;
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(isPlaying);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement) {
      this.#bindVideo(content);
      if (content.getAttribute('autoplay')) {
        // force autoplay for safari
        content.play();
      }
    } else {
      // No play/pause event available for this content (e.g. a YouTube/Vimeo iframe), so assume
      // it started playing on reveal.
      this.#setPlaying(true);
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    /** @type {HTMLVideoElement | null} */
    const video = this.querySelector('video');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else if (video) {
      // A rapid pause() right after this can reject the returned promise ("interrupted by a call
      // to pause()") - expected under fast repeated clicks, not an error to surface.
      video.play()?.catch(() => {});
    }

    // A <video> updates isPlaying/the hint itself via its own 'play' event (#bindVideo) once it
    // actually responds. Everything else (an iframe player, or e.g. a product-model with no
    // media element here at all) has no such event to rely on, so assume it responds.
    if (!video) this.#setPlaying(true);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    /** @type {HTMLVideoElement | null} */
    const video = this.querySelector('video');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else if (video) {
      video.pause();
    }

    // See playMedia(): a <video> drives isPlaying/the hint itself via its own 'pause' event.
    if (!video) this.#setPlaying(false);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
